import { trackEvent } from "../utils/track-event";
import type {
  EventListType,
  TrackingEvent,
  TrackingEventResponse,
} from "../utils/types";
import type {
  SegmentCatalogProduct,
  SegmentHomepageSingleCategoryProduct,
  SegmentProduct,
  SegmentProductListMeta,
} from "src/segment";
import type { BrandGateLevelInCustomerPlatform } from "src/types/graphql.d";

type DataLayerCategory = { id?: string; name?: string };

type ProductListViewedEventProps = {
  defaultSortBy?: string;
  numberOfItems?: number;
  productListMeta: SegmentProductListMeta;
  products: SegmentProduct[];
  resultId?: string;
  responseSourceType?: string | null;
  query?: string | null;
  gated?: boolean;
};

type ProductListViewedProductPayload = {
  brand: string;
  brand_id: string;
  category: unknown;
  category_id: unknown;
  image_url: string;
  labels: string[];
  name: string;
  position: number;
  price: number;
  product_discount: number;
  product_id: string;
  product_kids_gender?: unknown;
  product_shop_type?: unknown;
  regular_price: number;
  sizes_available: unknown;
  url: string;
  gate_level: BrandGateLevelInCustomerPlatform | null;
};

type ProductListViewedAnalyticsProps = {
  result_id?: string;
  default_sort_by?: string;
  list_format: EventListType;
  list_id: string;
  list_type: string;
  num_items?: number;
  shop_type?: string;
  path: string;
  response_source_type?: string | null;
  query?: string | null;
  products: ProductListViewedProductPayload[];
  gated?: boolean;
};

type ProductListViewedDataLayerProps = {
  event: "view_item_list";
  item_list_name: string;
  items: {
    item_id: string;
    item_name: string;
    item_category: { level: string; name: string; id: string }[];
    price: number;
    retail_price: number;
  }[];
};

export type ProductListViewedEvent = TrackingEvent<
  "Product List Viewed",
  ProductListViewedAnalyticsProps,
  ProductListViewedDataLayerProps
>;

function getDataLayerCategories(product: SegmentProduct) {
  const categories = (
    product as unknown as { categories?: DataLayerCategory[] }
  )?.categories;
  if (!Array.isArray(categories)) return [];
  return categories.map((category, index) => ({
    level: (index + 1).toString(),
    name: category?.name || "",
    id: category?.id || "",
  }));
}

export async function trackProductListViewedEvent({
  defaultSortBy,
  numberOfItems,
  productListMeta: { listFormat, listId, listType, shopType },
  products,
  responseSourceType,
  query,
  gated,
  resultId,
}: ProductListViewedEventProps): TrackingEventResponse {
  const path = typeof window !== "undefined" ? window.location.pathname : "";

  return await trackEvent("Product List Viewed", {
    props: {
      result_id: resultId,
      default_sort_by: defaultSortBy,
      list_format: listFormat as EventListType,
      list_id: listId,
      list_type: listType,
      num_items: numberOfItems,
      shop_type: shopType,
      path,
      response_source_type: responseSourceType,
      query,
      gated,
      products: products?.map((item) => {
        const {
          price: catalogProductPrice,
          regular_price,
          available_sizes,
        } = item as SegmentCatalogProduct;

        const { price_float, regular_price_float, sizes_available } =
          item as SegmentHomepageSingleCategoryProduct;

        const price = catalogProductPrice || price_float;
        const regularPrice = regular_price || regular_price_float;
        const sizesAvailable = available_sizes || sizes_available;

        return {
          brand: item.brand.name,
          brand_id: item.brand.id,
          category: item.translated_category_names,
          category_id: item.translated_category_ids,
          image_url: item.thumbnail,
          labels: (item.labels || []).map(({ value }) => value),
          name: item.name,
          position: item.position,
          price,
          product_discount:
            item?.product_discount ||
            Math.round((regularPrice - price + Number.EPSILON) * 100) / 100,
          product_id: item.id,
          product_kids_gender: item.product_shop_type?.includes("kids")
            ? item.product_kids_gender
            : undefined,
          product_shop_type: item.product_shop_type,
          regular_price: regularPrice,
          sizes_available: sizesAvailable,
          url: `/product/${item.slug}`,
          gate_level: item.gate?.level || null,
        };
      }),
    },
    dataLayerProps: {
      event: "view_item_list",
      item_list_name: listType,
      items: (products || []).map((product) => ({
        item_id: product.id,
        item_name: product.name,
        item_category: getDataLayerCategories(product),
        price: (product as SegmentCatalogProduct).price,
        retail_price: (product as SegmentCatalogProduct).regular_price,
      })),
    },
    withRequestSource: true,
  });
}
